/*!
 * lh-form-validation.js
 * Branded client-side form validation for Logan Hollowell.
 * Suppresses native browser validation bubbles; replaces with
 * on-brand inline error messages (Jost italic, warm rose, slide-in).
 */
(function () {
  'use strict';

  /* ── INJECTED STYLES ──────────────────────────────────────── */
  var CSS = [
    /* Error message */
    '.lhfv-error {',
    '  display: block;',
    '  font-family: "Jost", sans-serif;',
    '  font-size: 0.68rem;',
    '  font-weight: 300;',
    '  font-style: italic;',
    '  letter-spacing: 0.12em;',
    '  margin-top: 7px;',
    '  opacity: 0;',
    '  transform: translateY(-5px);',
    '  transition: opacity 0.28s ease, transform 0.28s ease;',
    '  pointer-events: none;',
    '  color: #9c5f5f;', /* warm rose — 4.8:1 on cream #f9f5ef */
    '}',
    /* Dark-background override (applied when form is on a dark section) */
    '.lhfv-dark-ctx .lhfv-error { color: #d4a0a0; }', /* 4.5:1 on #2c2218 */
    /* Visible state */
    '.lhfv-error.lhfv-vis {',
    '  opacity: 1;',
    '  transform: translateY(0);',
    '}',
    /* Invalid field: border turns rose */
    '.lhfv-invalid { border-bottom-color: #b07070 !important; }',
    /* Reduced motion */
    '@media (prefers-reduced-motion: reduce) {',
    '  .lhfv-error { transition: none; opacity: 1; transform: none; }',
    '}',
  ].join('\n');

  function injectStyles() {
    if (document.getElementById('lhfv-styles')) return;
    var style = document.createElement('style');
    style.id = 'lhfv-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* ── DARK CONTEXT DETECTION ──────────────────────────────── */
  function toLinear(c) {
    c /= 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  function relativeLuminance(r, g, b) {
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  }
  function isDark(el) {
    var node = el;
    while (node && node !== document.documentElement) {
      var bg = window.getComputedStyle(node).backgroundColor;
      var m = bg.match(/[\d.]+/g);
      /* Skip transparent backgrounds (alpha === 0) */
      if (m && m.length >= 3 && (+m[3] !== 0 || m.length === 3)) {
        return relativeLuminance(+m[0], +m[1], +m[2]) < 0.18;
      }
      node = node.parentElement;
    }
    return false;
  }

  /* ── ERROR MESSAGES ──────────────────────────────────────── */
  function getMessage(input) {
    var type = (input.type || '').toLowerCase();
    var v = input.validity;
    if (v.valueMissing) {
      if (type === 'email') return '\u2014 Enter your email address.';
      if (type === 'tel')   return '\u2014 Enter your phone number.';
      if (input.tagName === 'TEXTAREA') return '\u2014 This field cannot be left blank.';
      return '\u2014 This field is required.';
    }
    if (v.typeMismatch && type === 'email') return '\u2014 Enter a valid email address.';
    if (v.patternMismatch) return input.dataset.errorMsg || '\u2014 Please check this field.';
    return '\u2014 Please check this field.';
  }

  /* ── SHOW / CLEAR ERRORS ─────────────────────────────────── */
  function showError(input, msg) {
    input.classList.add('lhfv-invalid');
    input.setAttribute('aria-invalid', 'true');

    var id = 'lhfv-err-' + input.id;
    var span = document.getElementById(id);

    if (span) {
      span.textContent = msg;
      span.classList.add('lhfv-vis');
    } else {
      span = document.createElement('span');
      span.className = 'lhfv-error';
      span.id = id;
      span.setAttribute('role', 'alert');
      span.textContent = msg;
      input.insertAdjacentElement('afterend', span);
      /* Double rAF so the transition actually fires */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { span.classList.add('lhfv-vis'); });
      });
    }

    input.setAttribute('aria-describedby',
      (input.dataset.lhfvPrevDescribedby ? input.dataset.lhfvPrevDescribedby + ' ' : '') + id
    );
  }

  function clearError(input) {
    input.classList.remove('lhfv-invalid');
    input.removeAttribute('aria-invalid');
    var id = 'lhfv-err-' + input.id;
    var span = document.getElementById(id);
    if (span) span.remove();
    var prev = input.dataset.lhfvPrevDescribedby;
    if (prev) {
      input.setAttribute('aria-describedby', prev);
    } else {
      input.removeAttribute('aria-describedby');
    }
  }

  /* ── VALIDATE ONE FIELD ──────────────────────────────────── */
  function validateField(input) {
    var valid = input.validity.valid;
    /* Treat whitespace-only as empty for text inputs */
    if (valid && input.required && input.value && !input.value.trim()) {
      valid = false;
    }
    if (!valid) {
      showError(input, getMessage(input));
      return false;
    }
    clearError(input);
    return true;
  }

  /* ── WIRE A FORM ─────────────────────────────────────────── */
  function wireForm(form) {
    if (form.dataset.lhfvWired) return;
    form.dataset.lhfvWired = '1';
    form.setAttribute('novalidate', '');

    /* Dark context? Add class to form for CSS colour override */
    if (isDark(form)) form.classList.add('lhfv-dark-ctx');

    /* Store original aria-describedby so we can restore it on clear */
    form.querySelectorAll('input, textarea, select').forEach(function (f) {
      if (!f.id) return;
      var desc = f.getAttribute('aria-describedby');
      if (desc) f.dataset.lhfvPrevDescribedby = desc;

      f.addEventListener('input',  function () { if (f.classList.contains('lhfv-invalid')) clearError(f); });
      f.addEventListener('change', function () { if (f.classList.contains('lhfv-invalid')) clearError(f); });
    });

    form.addEventListener('submit', function (e) {
      var allValid = true;
      var firstBad = null;

      /* Validate all required fields */
      form.querySelectorAll('[required], [aria-required="true"]').forEach(function (f) {
        if (!f.id) return;
        if (!validateField(f)) {
          allValid = false;
          if (!firstBad) firstBad = f;
        }
      });

      /* Validate typed fields (email/tel) with a value but invalid format */
      form.querySelectorAll('input[type="email"], input[type="tel"]').forEach(function (f) {
        if (!f.id || !f.value.trim() || f.validity.valid) return;
        if (!validateField(f)) {
          allValid = false;
          if (!firstBad) firstBad = f;
        }
      });

      if (!allValid) {
        e.preventDefault();
        e.stopPropagation();
        if (firstBad) firstBad.focus();
      }
    });
  }

  /* ── INIT ────────────────────────────────────────────────── */
  function init() {
    injectStyles();
    document.querySelectorAll('form').forEach(wireForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
